import React, { useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { yupResolver } from '@hookform/resolvers/yup'

import useDidMount from '@divvy-web/hooks.usedidmount'
import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton from '@divvy-web/skylab.basicbutton'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import BillTermsAndConditionsPrompt from '@sputnik/components/BillTermsAndConditionsPrompt'
import { PRODUCT_CONST_STRINGS, URL } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError } from '@sputnik/utils/loggerUtils'

import {
  type EmailVerificationFormValidationSchema,
  emailVerificationFormValidationSchema,
} from './emailVerificationFormValidationSchema'
import styles from './styles.module.scss'

export const EmailVerification = () => {
  const [getClassName] = useNameFormatter('EmailVerification')
  const { search } = useLocation()
  const [searchParams] = useSearchParams()
  const { requestAuthCode } = useAuth()
  const showToast = useToast()
  const navigate = useNavigate()
  const { track, applicantData } = useWebTracking()

  const { handleSubmit, control, getValues } = useForm<EmailVerificationFormValidationSchema>({
    mode: 'all',
    defaultValues: {
      email: applicantData?.email ?? '',
    },
    resolver: yupResolver(emailVerificationFormValidationSchema),
  })

  const { isValid, isDirty, isSubmitting } = useFormState({ control })

  const { BILL_SPEND_AND_EXPENSE } = PRODUCT_CONST_STRINGS
  const { DIVVY_PROD } = URL
  const onSubmit = async ({ email }: EmailVerificationFormValidationSchema) => {
    track('se_signup__email_verification__submitted', {
      button_id: 'continue',
    })
    try {
      await requestAuthCode(email)
      navigate({ pathname: 'verify', search })
    } catch (error) {
      logError(error, {
        attributes: {
          action: 'requestAuthCode',
          message: error ?? 'None',
          result: 'Error requesting auth code',
        },
        eventName: 'requestAuthCode',
      })

      track('se_signup__error_message__fired', {
        error_field: 'email',
        error_message_text: 'Error requesting auth code',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Error requesting auth code'
          id='sputnik.EmailVerification__+nsFGL'
        />,
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
    }
  }

  /* tracking start */
  useEffect(() => {
    if (isValid && isDirty) {
      track('se_signup__email_verification_email_id__entered', {
        email_field_has_value: !!getValues('email'),
      })
    }
  }, [isValid, isDirty, track, getValues])

  useEffect(() => {
    track('se_signup__email_verification__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  /* tracking end */

  useDidMount(() => {
    // auto code request logic if email is provided in the URL
    const queryParamEmail = searchParams.get('email')
    if (queryParamEmail) {
      onSubmit({ email: queryParamEmail })
    }
  })

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <form
        className={styles[getClassName('form')]}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles[getClassName('title')]}>
          <FormattedMessage
            defaultMessage='Start your credit application—or pick up where you left off'
            id='sputnik.EmailVerification__fCtz2Z'
          />
        </div>
        <FormTextInput
          className='fs-mask'
          control={control}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Enter your business email'
                id='sputnik.EmailVerification__7/X4+G'
              />
            </span>
          }
          name='email'
        />
        <BasicButton
          buttonType='submit'
          className={styles[getClassName('submit')]}
          disabled={!isValid || isSubmitting}
          showSpinner={isSubmitting}
        >
          <FormattedMessage
            defaultMessage='Continue'
            id='sputnik.EmailVerification__acrOoz'
          />
        </BasicButton>
      </form>
      <BillTermsAndConditionsPrompt />
      <div className={styles[getClassName('sign-in')]}>
        <FormattedMessage
          defaultMessage='Already use {BILL_SPEND_AND_EXPENSE}?'
          id='sputnik.EmailVerification__kSLK+v'
          values={{
            BILL_SPEND_AND_EXPENSE,
          }}
        />
        <TextLink
          href={DIVVY_PROD}
          size={TextLinkSizes.REGULAR}
          target='_blank'
          onClick={() => {
            track('se_signup__email_verification_sign_in__submitted')
          }}
        >
          <FormattedMessage
            defaultMessage='Sign in'
            id='sputnik.EmailVerification__SQJto2'
          />
        </TextLink>
      </div>
    </div>
  )
}

export default EmailVerification
